import React, { isValidElement, useMemo } from "react";
import { ComposerProps } from "./types";
import { Background } from "../Background/Background";
import { Image } from "../Image/Image";
import type { ImageProps } from "../Image/types";
import cx from "components/src/utils/cx";
import { positionMap } from "shared/src/styles/positionStyles";
import { addProps } from "shared/src/utils/addProps";
import { useIdRef } from "shared/src/hooks/useIdRef";

export const justifyMap = (xOffset = "0px", yOffset = "0px") => ({
  center: {
    top: "50%",
    left: "50%",
    transform: `translate(calc(-50% + ${xOffset}), calc(-50% + ${yOffset}))`,
  },
  right: {
    top: "50%",
    right: "0",
    transform: `translate(${xOffset}, calc(-50% + ${yOffset}))`,
  },
  left: {
    top: "50%",
    left: "0",
    transform: `translate(${xOffset}, calc(-50% + ${yOffset}))`,
  },
  topLeft: {
    top: "0",
    left: "0",
    transform: `translate(${xOffset}, ${xOffset})`,
  },
  bottomLeft: {
    bottom: "0",
    left: "0",
    transform: `translate(${xOffset}, ${yOffset})`,
  },
  topRight: {
    top: "0",
    right: "0",
    transform: `translate(${xOffset}, ${yOffset})`,
  },
  bottomRight: {
    bottom: "0",
    right: "0",
    transform: `translate(${xOffset}, ${yOffset})`,
  },
  topCenter: {
    top: "0",
    left: "50%",
    transform: `translate(calc(-50% + ${xOffset}), ${yOffset})`,
  },
  bottomCenter: {
    bottom: "0",
    left: "50%",
    transform: `translate(calc(-50% + ${xOffset}), ${yOffset})`,
  },
});

export const Composer = ({
  positioning = { position: "relative" },
  elements,
  shape,
  className = "",
  style,
  ...rest
}: ComposerProps) => {
  const _id = useIdRef({ str: "cui__composer-"});
  const wrapperClasses = useMemo(() => {
    return cx(
      {
        [positionMap[positioning?.position ?? "relative"]]: true,
        [className]: true,
        "cui-rounded-[9999px] cui-overflow-hidden": shape === "circle"
      },
      "cui__composer"
    );
  }, [positioning, shape]);

  const processedElements = useMemo(() => {
    return elements.map((element, i) => {

      const _justifyBg = getJustify(element);
      const _justifyFg = getJustify(element?.fg);
      const _fgStyleObj = element?.fg && isValidElement(element?.fg?.element) && element?.fg?.element?.props?.style;
      if (isValidElement(element?.fg?.element) && typeof _fgStyleObj === "object" && "transform" in _fgStyleObj) _justifyFg.transform += ` ${_fgStyleObj?.transform}`;

      const fg =
        element?.fg && isValidElement(element?.fg?.element) ? (
          addProps(element?.fg?.element, { key: `${_id}-${i}`, style: { ..._justifyFg, position: "absolute", opacity: element?.fg?.opacity ?? 1, mixBlendMode: element?.fg?.blend ?? ""  }})
        ) : (
          <Image
            {...(element?.fg?.element as ImageProps)}
            className={`cui-absolute`}
            style={{
              ..._justifyFg,
              opacity: element?.fg?.opacity ?? 1,
              mixBlendMode: element?.fg?.blend ?? ""  
            }}
            key={`${_id}-${i}`}
          />
        );

      const bgProps = element?.bg ? element?.bg : { primaryColor: "rgba(0,0,0,0)" };
      const bgStyle = {
        ..._justifyBg,
        opacity: element?.opacity ?? 1,
        mixBlendMode: element?.blend ?? ""
      };

      const _computedBgClasses = `${element?.bg?.className ?? ""} ${element?.elementClasses ?? ""}`.trim();

      if (element?.bg === null) return fg;

      return (
        <Background
        {...bgProps}
        position="absolute"
        height={element?.positioning?.height ?? "100%"}
        width={element?.positioning?.width ?? "100%"}
        wrapperClasses={_computedBgClasses}
        style={bgStyle}
        key={`${_id}-bg-${i}`}
      >
        {fg}
      </Background>
      );
    });
  }, [elements]);
  return (
    <div className={wrapperClasses} style={{ ...style, ...positioning }} {...rest}>
      {processedElements}
    </div>
  );
};

// Get justify CSS props
function getJustify(el) {
  return el ? justifyMap(
    el?.positioning?.justifyOffsetX,
    el?.positioning?.justifyOffsetY
  )[el?.positioning?.justify ?? "center"] : {}
}